import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DataList } from "@/common/DataList";
import { getFullStreetNameFromContactAddress } from "@/shared/utils";
import type { GeneralInfoTabFormValues } from "@/entities/general-info-tab-content/model";
import { AddressField } from "./AddressField";

type Props = {
  personId: string;
  isEditMode: boolean;
  handleEditMode: () => void;
  onSubmit: (data: GeneralInfoTabFormValues) => void;
  disabled?: boolean;
};

export const AddressesList = ({
  personId,
  isEditMode,
  handleEditMode,
  onSubmit,
  disabled,
}: Props) => {
  const { t } = useTranslation();

  const { control, getValues, trigger } = useFormContext<GeneralInfoTabFormValues>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "addresses",
  });

  const onBlur = async () => {
    const isValid = await trigger("addresses");

    if (isValid) {
      const formData = getValues();
      onSubmit(formData);
    }
  };

  const onChange = () => {
    trigger("addresses");
  };

  const addHandler = () => {
    if (!isEditMode) {
      handleEditMode();
    }

    setTimeout(() => {
      append({
        addressType: { name: "" },
        addressCity: "",
        addressCountry: "",
        addressStreet1: "",
        addressStreet2: "",
        addressPostalCode: "",
        id: undefined,
        personId,
      });
    }, 0);
  };

  const removeHandler = async (index: number) => {
    remove(index);

    const isValid = await trigger("addresses");

    if (isValid) {
      const formData = getValues();
      onSubmit(formData);
    }
  };

  return (
    <DataList
      data={fields.map((item) => ({
        id: item?.id ?? "",
        label: item?.addressType?.name ?? "",
        value:
          getFullStreetNameFromContactAddress({
            ...item,
            addressType: { name: item?.addressType?.name ?? "" },
            addressStreet1: item?.addressStreet1 ?? null,
            addressStreet2: item?.addressStreet2 ?? null,
            addressCity: item?.addressCity ?? null,
            addressCountry: item?.addressCountry ?? null,
            addressPostalCode: item?.addressPostalCode ?? null,
            personId: item?.personId ?? null,
          }) ?? "",
      }))}
      onAddClick={addHandler}
      onTrashClick={removeHandler}
      isEdit={isEditMode}
      title={t("contactsPage.generalInfo.address", { count: 2 })}
      editNode={(index) => <AddressField index={index} onBlur={onBlur} onChange={onChange} />}
      disabled={disabled}
      hint={isEditMode ? t("contactsPage.editAddressHint") : undefined}
    />
  );
};
